<template>
  <div>
    <v-text-field
      hide-details
      v-model="searchString"
      v-bind:placeholder="$t('start_typing_to_search')"
      @input="searchItems"
    ></v-text-field>
    <div class="loading-spinner text-center mt-2 mb-2" v-if="isDataLoading">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>
    <div class="list_options" v-if="filteredList.length > 0">
      <v-checkbox
        v-for="item in filteredList"
        :key="getItemValue(item)"
        v-model="selectedValues"
        :label="item.title"
        :value="getItemValue(item)"
      ></v-checkbox>
    </div>
    <div v-else class="text-center">{{ $t("Empty data") }}</div>
  </div>
</template>

<script>
import ApiService from "@/services/apiService";
import { logInfo, logError } from "@/utils";

export default {
  name: "filterableAjaxFieldCheckboxesDynamic",
  props: {
    // Use object syntax for props when you need more than just names
    value: {
      type: Array, // Assuming 'value' prop should be an array of selected IDs
      default: () => []
    },
    apiUrl: {
      type: String,
      required: false
    },
    columnName: {
      type: String,
      required: true
    },
    isFullText: {
      type: Boolean,
      default: false
    },
    criteria: {
      type: Object,
      default: () => ({})
    },
    isSearch: {
      type: Boolean,
      default: false
    },
    valueField: {
      // Correct way to define 'valueField' prop
      type: String,
      required: true
    },
    // New prop to pass current filter state
    currentFilters: {
      type: Object,
      default: () => ({})
    }
  },
  data: function() {
    return {
      isDataLoading: false,
      searchString: "",
      filteredList: [],
      selectedValues: [],
      lastFilterHash: null // To track filter changes
    };
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        this.selectedValues = Array.isArray(newVal) ? newVal : [];
      }
    },
    selectedValues(newVal) {
      this.$emit("input", newVal);
    },
    currentFilters: {
      deep: true,
      immediate: true,
      handler(newFilters) {
        // Create a hash of the current filters to detect changes
        const filterHash = this.createFilterHash(newFilters);
        
        // Only refresh if filters actually changed
        if (filterHash !== this.lastFilterHash) {
          this.lastFilterHash = filterHash;
          // Delay the API call slightly to avoid rapid successive calls
          this.refreshFilterOptions();
        }
      }
    },
    isSearch: function(newVal, oldVal) {
      if (this.columnName === "portal_photo_name" && newVal) {
        this.setSearchString("");
      }
    }
  },
  methods: {
    getItemValue(item) {
      return item[this.valueField] || item.id;
    },
    
    createFilterHash(filters) {
      // Create a simple hash of the filter state
      return JSON.stringify(filters);
    },
    
    setSearchString(str) {
      this.searchString = str;
      this.getDataFromApi(this.searchString);
    },
    
    getFetchUrl(searchStr) {
      if (this.apiUrl.trim().length) {
        let fetch_url = this.apiUrl;
        let criteria = [
          "column=" + this.columnName,
          "filterValue=" + searchStr
        ];
        
        if (this.isFullText) {
          criteria.push("isFullText=true");
        }
        
        // Add current filter model for dynamic filtering
        if (this.currentFilters && Object.keys(this.currentFilters).length > 0) {
          // Convert filters to the format expected by the backend
          const filterModel = this.buildFilterModel(this.currentFilters);
          if (filterModel.length > 0) {
            criteria.push("filterModel=" + encodeURIComponent(JSON.stringify(filterModel)));
          }
        }
        
        // Legacy criteria handling
        if (this.criteria && this.criteria?.brand_name) {
          criteria.push(
            "idBrands=" +
              Object.values(this.criteria.brand_name)
                .filter(el => el)
                .join(",")
          );
        }
        if (this.criteria && this.criteria?.survey_name) {
          criteria.push(
            "idSurveys=" +
              Object.values(this.criteria.survey_name)
                .filter(el => el)
                .join(",")
          );
        }
        
        fetch_url += "?" + criteria.join("&");
        return fetch_url;
      }
    },
    
    buildFilterModel(filters) {
      const filterModel = [];
      
      // Convert the current filter state to the format expected by backend
      for (const [columnField, filterValue] of Object.entries(filters)) {
        if (filterValue && Array.isArray(filterValue) && filterValue.length > 0) {
          filterModel.push({
            columnField,
            filterValue
          });
        }
      }
      
      return filterModel;
    },
    
    async searchItems() {
      this.getDataFromApi(this.searchString);
    },
    
    async getDataFromApi(searchString) {
      let fetch_url = this.getFetchUrl(searchString);

      this.isDataLoading = true;
      
      try {
        let response = await ApiService.get(fetch_url);

        if (response.data && response?.data.length) {
          console.log("inside if");
          let list = Array.isArray(response.data.data) ? response.data.data : Array.isArray(response.data) ? response.data : [];
          const seen = new Set();
          this.filteredList = list.filter(item => {
            const val = this.getItemValue(item);
            if (seen.has(val)) return false;
            seen.add(val);
            return true;
          });
        } else {
          this.filteredList = [];
        }
        this.isDataLoading = false;
      } catch (error) {
        console.log("Catched error: ", error);
        this.filteredList = [];
        this.isDataLoading = false;
      }
    },
    
    refreshFilterOptions() {
      // Refresh the filter options based on current filter state
      this.getDataFromApi(this.searchString);
    },
    
    init() {
      this.getDataFromApi("");
    }
  },
  async mounted() {
    this.init();
  }
};
</script>

<style>
.list_options {
  max-height: 300px;
  overflow-y: scroll;
}
.list_options .v-input--selection-controls {
  margin-top: 5px;
}
</style>
